/**
 * ADD CUTICLE CARE MODAL
 *
 * Features:
 * - Quick Search (Brand + Name) to find existing items
 * - Photos section at top with multi-photo support
 * - Cuticle Care-specific fields (type, size, scent)
 * - Destash section at bottom
 */

import React, { useState } from 'react';
import { X, Heart, Info, Trash2, Repeat, Store, Upload, Star, Plus, ExternalLink, GripVertical, Pencil } from 'lucide-react';
import { SortablePhotoGrid } from '../common/SortablePhotoGrid';
import PhotoEditModal, { buildPhotoSummary } from '../common/PhotoEditModal';
import { AppIcons } from '../../constants/icons';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { getCurrentUser } from '../../firebase/auth';
import { buildDestashFields, clearDestashFields } from '../../services/destashDenormalization';
import { uploadPhotos as uploadPhotosService } from '../../services/photoUploadService';
import { itemDocKeys } from '../../utils/itemDocKeys';
import { normalizeTypography } from '../../utils/normalizeTypography';
import PhotoRightsAttestation from '../common/PhotoRightsAttestation';
import BrandAutocomplete from '../common/BrandAutocomplete';
import CustomTagsEditor from '../common/CustomTagsEditor';
import { RequiredMark, RequiredLegend } from '../common';
import ReleaseInfoSection, {
  type ReleaseInfoValue,
  emptyReleaseInfo,
  releaseInfoSaveShape,
} from '../common/ReleaseInfoSection';
import { useFirestoreBrands } from '../../services/useFirestoreBrands';
import { useSellerCurrency } from '../../hooks/useSellerCurrency';
import { usePinnedCurrencies } from '../../hooks/usePinnedCurrencies';
import { getCurrencyInfo } from '../../constants/currencies';
import CurrencyPicker from '../common/CurrencyPicker';
import StorageUpgradeModal from '../StorageUpgradeModal';
import { CategoryColors, PriorityColors } from '../../constants/categoryColors';
import ConditionSelector from '../common/ConditionSelector';
import PublicItemSearch from '../common/PublicItemSearch';
import type { SearchResult } from '../common/PublicItemSearch';
import type { CollectionItem } from '../../types';
import { ACCEPTED_IMAGE_TYPES } from '../../utils/imageUtils';

interface AddCuticleCareModalProps {
  onClose: () => void;
  onSuccess?: (newItem: CollectionItem) => void;
  existingBrands?: string[];
  userBrandCategories?: Record<string, any>;
}

interface PendingPhoto {
  id: string;
  file: File;
  preview: string;
  caption: string;
  isDefault: boolean;
  skinTone?: string;
  lighting?: string;
}

export default function AddCuticleCareModal({ onClose, onSuccess, existingBrands = [], userBrandCategories = {} }: AddCuticleCareModalProps) {
  const firestoreBrands = useFirestoreBrands();
  const [name, setName] = useState('');
  const [brand, setBrand] = useState('');
  const [cuticleCareType, setCuticleCareType] = useState('');
  const [size, setSize] = useState('');
  const [scent, setScent] = useState('');
  // Color is now a separate field (previously 'color' Firestore field was
  // reused as scent via a hack). Legacy data on existing items has been
  // migrated color→scent so this field starts empty for new items but
  // can record a tinted balm's actual color.
  const [color, setColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [price, setPrice] = useState('');
  // Per-item currency for THIS item's purchase price (record-keeping).
  // Distinct from `currency` (seller's profile currency for destash
  // display). Falls back to seller currency until the user picks a code.
  const [purchaseCurrency, setPurchaseCurrency] = useState<string>('');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [storageLocation, setStorageLocation] = useState('');
  const [customTags, setCustomTags] = useState<string[]>([]);
  const [releaseInfo, setReleaseInfo] = useState<ReleaseInfoValue>(emptyReleaseInfo());
  const [publicNotes, setPublicNotes] = useState('');
  const [privateNotes, setPrivateNotes] = useState('');
  const [pendingPhotos, setPendingPhotos] = useState<PendingPhoto[]>([]);
  const [editingPhotoId, setEditingPhotoId] = useState<string | null>(null);
  const [wasPreFilled, setWasPreFilled] = useState(false);
  const [onWishlist, setOnWishlist] = useState(false);
  const [isoPriority, setIsoPriority] = useState<'iso' | 'diso' | 'unicorn'>('iso');
  const [isFavorite, setIsFavorite] = useState(false);
  const [isRareLE, setIsRareLE] = useState(false);
  const [needsInfo, setNeedsInfo] = useState(false);
  // Private polish — hides from every public surface (public profile,
  // brand pages, community aggregations, Discover). Marketplace is
  // unaffected: if user also toggles For Destash, item still appears on
  // Marketplace (with a warning near the toggle explaining that).
  const [isPrivate, setIsPrivate] = useState(false);
  // Seller's chosen currency from profile. Drives the symbol shown in the
  // price inputs so a EUR seller doesn't see "$" next to their price. The
  // save side (buildDestashFields) reads the same profile field, so what
  // the seller sees here matches what buyers see on Marketplace.
  const currency = useSellerCurrency();
  const { pinnedCurrencies, setPinnedCurrencies } = usePinnedCurrencies();
  const purchaseSymbol = purchaseCurrency
    ? getCurrencyInfo(purchaseCurrency).symbol
    : currency.symbol;
  const purchaseSymbolPad =
    purchaseSymbol.length <= 1 ? 'pl-7' : purchaseSymbol.length <= 2 ? 'pl-10' : 'pl-12';
  const symbolPad =
    currency.symbol.length <= 1 ? 'pl-7' : currency.symbol.length <= 2 ? 'pl-10' : 'pl-12';

  const [isForDestash, setIsForDestash] = useState(false);
  const [destashCondition, setDestashCondition] = useState('');
  const [destashPrice, setDestashPrice] = useState('');
  const [isOpenToSwaps, setIsOpenToSwaps] = useState(false);
  const [destashNotes, setDestashNotes] = useState('');
  const [destashQuantityAvailable, setDestashQuantityAvailable] = useState(1);
  const [showListedSuccess, setShowListedSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [photoRightsConfirmed, setPhotoRightsConfirmed] = useState(false);
  const [showStorageUpgradeModal, setShowStorageUpgradeModal] = useState(false);

  const handleSearchSelect = (result: SearchResult) => {
    setBrand(result.brand);
    setName(result.name);
    setWasPreFilled(true);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const newPhotos: PendingPhoto[] = Array.from(files).map((file, index) => ({
      id: `${Date.now()}-${index}`,
      file,
      preview: URL.createObjectURL(file),
      caption: '',
      isDefault: pendingPhotos.length === 0 && index === 0
    }));
    setPendingPhotos([...pendingPhotos, ...newPhotos]);
    e.target.value = '';
  };

  const updatePendingPhoto = (id: string, updates: Partial<PendingPhoto>) => {
    setPendingPhotos(photos => photos.map(p => p.id === id ? { ...p, ...updates } : p));
  };

  const setDefaultPhoto = (id: string) => {
    setPendingPhotos(photos => photos.map(p => ({ ...p, isDefault: p.id === id })));
  };

  const removePendingPhoto = (id: string) => {
    setPendingPhotos(photos => {
      const filtered = photos.filter(p => p.id !== id);
      if (filtered.length > 0 && !filtered.some(p => p.isDefault)) filtered[0].isDefault = true;
      return filtered;
    });
  };

  const uploadPhotos = async (userId: string) => {
    if (pendingPhotos.length === 0) return { photos: [], blocked: false };

    const result = await uploadPhotosService(
      userId,
      pendingPhotos,
      'cuticleCare',
      setUploadProgress,
      undefined,
      photoRightsConfirmed
    );

    if (result.storageBlocked) {
      setShowStorageUpgradeModal(true);
      return { photos: [], blocked: true };
    }

    if (result.errors.length > 0) {
      console.warn('Some photos failed to upload:', result.errors);
    }

    return { photos: result.photos, blocked: false };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const currentUser = getCurrentUser();
    if (!currentUser) { alert('Please log in first!'); return; }
    if (isForDestash && (!destashCondition || !destashPrice)) { alert('Please complete destash fields'); return; }

    setIsSubmitting(true);
    try {
      let photos = [] as any[];
      if (pendingPhotos.length > 0) {
        const uploadResult = await uploadPhotos(currentUser.uid);
        if (uploadResult.blocked) {
          setIsSubmitting(false);
          return;
        }
        photos = uploadResult.photos;
      }
      // Normalize smart-quote / typographic-dash variants down to plain
      // ASCII so an iOS user typing "Lil’ Sweet" writes the same string
      // as an Android user typing "Lil' Sweet". Prevents typography-only
      // duplicates from ever entering Firestore. Both `name` and `brand`
      // flow into itemDocKeys() too, so the derived slugs stay consistent.
      const normalizedBrand = normalizeTypography(brand.trim());
      const normalizedName = normalizeTypography(name.trim());
      const itemData: any = {
        itemType: 'cuticle_care', name: normalizedName, brand: normalizedBrand, userId: currentUser.uid, quantity,
        ...itemDocKeys(normalizedBrand, normalizedName),
        tags: customTags, photos, ...releaseInfoSaveShape(releaseInfo),
        onWishlist, ...(onWishlist && { isoPriority }), isFavorite, isRareLE, needsInfo, ...(isPrivate && { isPrivate: true }), isForDestash,
        ...(isForDestash && { destashCondition, destashPrice: parseFloat(destashPrice), destashQuantityAvailable, isOpenToSwaps, destashStatus: 'available', destashDate: new Date().toISOString(), ...(destashNotes.trim() && { destashNotes: destashNotes.trim() }) }),
        // Denormalized seller currency + ships-to so Marketplace can filter without a user-doc join.
        ...(isForDestash ? await buildDestashFields(currentUser.uid) : clearDestashFields()),
        timesUsed: 0, lastUsedDate: null, isDestashed: false, dateAdded: Timestamp.now(), updatedAt: Timestamp.now()
      };
      if (cuticleCareType) itemData.cuticleCareType = cuticleCareType;
      if (size.trim()) itemData.size = size.trim();
      if (scent.trim()) itemData.scent = scent.trim();
      if (color.trim()) itemData.color = color.trim();
      if (price) itemData.price = parseFloat(price);
      if (price && purchaseCurrency) itemData.priceCurrency = purchaseCurrency;
      if (purchaseDate) itemData.purchaseDate = new Date(purchaseDate);
      if (storageLocation.trim()) itemData.storage = storageLocation.trim();
      if (publicNotes.trim()) itemData.publicNotes = publicNotes.trim();
      if (privateNotes.trim()) itemData.notes = privateNotes.trim();

      const docRef = await addDoc(collection(db, 'users', currentUser.uid, 'items'), itemData);

      const newItem: CollectionItem = {
        id: docRef.id,
        ...itemData,
        dateAdded: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      } as CollectionItem;

      pendingPhotos.forEach(p => URL.revokeObjectURL(p.preview));
      if (onSuccess) onSuccess(newItem);

      if (isForDestash) {
        setShowListedSuccess(true);
      } else {
        onClose();
      }
    } catch (error) {
      console.error('Error adding cuticle care item:', error);
      alert('Failed to add item. Please try again.');
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="modal-light max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {showListedSuccess ? (
          <div className="p-6">
            <div className="text-center py-8">
              <div className="text-6xl mb-4">🎉</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2 font-heading">
                Listed on Marketplace!
              </h2>
              <p className="text-gray-600 mb-6 font-body">
                Your cuticle care item has been added to your collection and is now visible to other collectors.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href="/marketplace?tab=destash"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-green-500 text-white rounded-lg font-bold hover:bg-green-600 transition-default"
                >
                  <ExternalLink className="w-5 h-5" />
                  View in Marketplace
                </a>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-100 transition-default"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        ) : (
        <>
        <div className="modal-header-light">
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded-full text-xs font-bold text-white ${CategoryColors.cuticle_care.bg}`}>Cuticle Care</span>
            <h2 className="text-2xl font-bold text-light-primary font-heading">Add Cuticle Care</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-default"><X className="icon-base" /></button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body-light space-y-6">
          {/* Quick Search */}
          <div className="space-y-4">
            <h3 className="section-header-light font-body">🔍 Quick Search</h3>
            <PublicItemSearch itemType="cuticle_care" onSelect={handleSearchSelect} />
            {wasPreFilled && (
              <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                <span className="text-green-600 text-sm">✓ Form pre-filled!</span>
                <button type="button" onClick={() => setWasPreFilled(false)} className="text-green-500 hover:text-green-700"><X className="icon-sm" /></button>
              </div>
            )}
          </div>

          {/* Photos */}
          <div className="space-y-4">
            <h3 className="section-header-light font-body">📸 Photos</h3>
            <label className={`flex items-center justify-center gap-2 px-4 py-8 border-2 border-dashed border-gray-300/60 rounded-lg cursor-pointer hover:${CategoryColors.cuticle_care.border} hover:${CategoryColors.cuticle_care.light} transition-default`}>
              <Upload className="icon-base text-light-muted" /><span className="text-light-secondary">Click to upload photos</span>
              <input type="file" accept={ACCEPTED_IMAGE_TYPES} multiple onChange={handleFileSelect} className="hidden" />
            </label>
            {pendingPhotos.length > 0 && (
              <div className="space-y-4">
                <SortablePhotoGrid
                  items={pendingPhotos}
                  onReorder={setPendingPhotos}
                  className="space-y-4"
                >
                  {(photo, _index, dragHandleProps) => (
                    <div className={`border rounded-lg p-4 ${photo.isDefault ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300/60'}`}>
                      <div className="flex gap-4">
                        <div className="relative w-24 h-24 flex-shrink-0">
                          <img src={photo.preview} alt="Preview" className="w-full h-full object-cover rounded-lg" />
                          {photo.isDefault && <div className="absolute -top-2 -right-2 bg-yellow-400 rounded-full p-1"><Star className="icon-sm text-white fill-white" /></div>}
                          <button
                            type="button"
                            {...dragHandleProps}
                            className="absolute top-1 right-1 p-1 bg-white/90 rounded-full cursor-grab active:cursor-grabbing touch-none"
                            title="Drag to reorder"
                            aria-label="Drag to reorder"
                          >
                            <GripVertical className="w-4 h-4 text-gray-600" />
                          </button>
                        </div>
                        <div className="flex-1 space-y-2">
                          <div className="p-2 bg-white flex items-start gap-2">
                            <div className="flex-1 min-w-0 text-xs text-gray-700">
                              {buildPhotoSummary(photo) || (
                                <span className="text-gray-400 italic">No details yet.</span>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => setEditingPhotoId(photo.id)}
                              className="flex-shrink-0 px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200 flex items-center gap-1"
                              title="Edit caption, credit, skin tone, lighting"
                            >
                              <Pencil className="w-3 h-3" />
                              Edit
                            </button>
                          </div>
                          <div className="flex gap-2">
                            {!photo.isDefault && <button type="button" onClick={() => setDefaultPhoto(photo.id)} className="flex items-center gap-1 px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 transition-default font-body"><Star className="icon-xs" /> Set default</button>}
                            <button type="button" onClick={() => removePendingPhoto(photo.id)} className="flex items-center gap-1 px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 transition-default font-body"><Trash2 className="w-3 h-3" /> Remove</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </SortablePhotoGrid>
                <PhotoEditModal
                  open={editingPhotoId !== null}
                  photo={(() => {
                    const p = pendingPhotos.find((x) => x.id === editingPhotoId);
                    if (!p) return null;
                    return {
                      imageUri: p.preview,
                      caption: p.caption,
                      skinTone: p.skinTone,
                      lighting: p.lighting,
                    };
                  })()}
                  onClose={() => setEditingPhotoId(null)}
                  onSave={(patch) => {
                    if (!editingPhotoId) return;
                    updatePendingPhoto(editingPhotoId, {
                      caption: patch.caption,
                      skinTone: patch.skinTone,
                      lighting: patch.lighting,
                    });
                  }}
                />
                <label className={`flex items-center justify-center gap-2 px-4 py-3 border border-dashed border-gray-300/60 rounded-lg cursor-pointer hover:${CategoryColors.cuticle_care.border} hover:${CategoryColors.cuticle_care.light} transition-default`}>
                  <Plus className="icon-sm text-light-muted" /><span className="text-sm text-light-secondary font-body">Add more</span>
                  <input type="file" accept={ACCEPTED_IMAGE_TYPES} multiple onChange={handleFileSelect} className="hidden" />
                </label>
              </div>
            )}
            <PhotoRightsAttestation
              pendingCount={pendingPhotos.length}
              confirmed={photoRightsConfirmed}
              onChange={setPhotoRightsConfirmed}
            />
          </div>

          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="section-header-light font-body">✨ Basic Info</h3>
            <RequiredLegend />
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium text-light-secondary mb-1">Brand <RequiredMark /></label><BrandAutocomplete value={brand} onChange={setBrand} existingBrands={existingBrands} userBrandCategories={userBrandCategories} firestoreBrands={firestoreBrands} placeholder="e.g., CND, Hive of Beauty" required /></div>
              <div><label className="block text-sm font-medium text-light-secondary mb-1">Name <RequiredMark /></label><input type="text" required value={name} onChange={(e) => setName(e.target.value)} className="input-light font-body" placeholder="e.g., SolarOil" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium text-light-secondary mb-1">Type</label>
                <select value={cuticleCareType} onChange={(e) => setCuticleCareType(e.target.value)} className="select-light font-body">
                  <option value="">Select type...</option>
                  <option value="Oil">Oil</option>
                  <option value="Cream">Cream</option>
                  <option value="Serum">Serum</option>
                  <option value="Balm">Balm</option>
                  <option value="Remover">Remover</option>
                  <option value="Treatment">Treatment</option>
                  <option value="Softener">Softener</option>
                  <option value="Exfoliator">Exfoliator</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div><label className="block text-sm font-medium text-light-secondary mb-1">Size</label><input type="text" value={size} onChange={(e) => setSize(e.target.value)} className="input-light font-body" placeholder="e.g., 3.7ml, 15ml" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium text-light-secondary mb-1">Scent / Fragrance</label><input type="text" value={scent} onChange={(e) => setScent(e.target.value)} className="input-light font-body" placeholder="e.g., Lavender, Unscented" /></div>
              <div><label className="block text-sm font-medium text-light-secondary mb-1">Color (if tinted)</label><input type="text" value={color} onChange={(e) => setColor(e.target.value)} className="input-light font-body" placeholder="e.g., Pink tint" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium text-light-secondary mb-1">Quantity</label><input type="number" min="1" value={quantity} onChange={(e) => setQuantity(parseInt(e.target.value) || 1)} className="input-light font-body" /></div>
              <div></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-light-secondary mb-1">Purchase Price</label>
                <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-light-muted">{purchaseSymbol}</span><input type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className={`input-light ${purchaseSymbolPad} font-body`} placeholder="0.00" /></div>
                <p className="mt-1 text-xs text-gray-700 italic">
                  Defaults to your profile currency. Change per item if you paid in a different currency.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-light-secondary mb-1">
                  Currency
                </label>
                <CurrencyPicker
                  value={purchaseCurrency}
                  onChange={setPurchaseCurrency}
                  pinnedCurrencies={pinnedCurrencies}
                  onPinChange={setPinnedCurrencies}
                  sellerDefault={currency.code}
                />
              </div>
              <div><label className="block text-sm font-medium text-light-secondary mb-1">Storage</label><input type="text" value={storageLocation} onChange={(e) => setStorageLocation(e.target.value)} className="input-light font-body" placeholder="e.g., Nail desk" /></div>
            </div>
            {/* Purchase Date */}
            <div>
              <label className="block text-sm font-medium text-light-secondary mb-1">
                Purchase Date
              </label>
              <input
                type="date"
                value={purchaseDate}
                onChange={(e) => setPurchaseDate(e.target.value)}
                className="input-light font-body"
              />
            </div>
            <div><label className="block text-sm font-medium text-light-secondary mb-1">Custom tags</label><CustomTagsEditor value={customTags} onChange={setCustomTags} placeholder="Add a tag and press Enter or comma" /></div>

            <div className="space-y-4">
              <h3 className="section-header-light font-body">📅 Release Info</h3>
              <ReleaseInfoSection value={releaseInfo} onChange={setReleaseInfo} datalistId="cuticle-brand-collection-suggestions" />
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-4">
            <h3 className="section-header-light font-body">📝 Notes</h3>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium text-light-secondary mb-1">Public Notes</label><textarea value={publicNotes} onChange={(e) => setPublicNotes(e.target.value)} rows={3} className="textarea-light font-body" placeholder="Visible to others..." /></div>
              <div><label className="block text-sm font-medium text-light-secondary mb-1">Private Notes 🔒</label><textarea value={privateNotes} onChange={(e) => setPrivateNotes(e.target.value)} rows={3} className="w-full px-4 py-2 border border-yellow-200 bg-yellow-50 rounded-lg focus:ring-2 focus:ring-amber-500 resize-none font-body" placeholder="Only you see these..." /></div>
            </div>
          </div>

          {/* Toggles */}
          <div className="space-y-4">
            <h3 className="section-header-light font-body">🎛️ Toggles</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <button type="button" onClick={() => setIsFavorite(!isFavorite)} className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-default ${isFavorite ? 'bg-pink-100 text-pink-800 border border-pink-300' : 'btn-light-bordered'}`}>
                <Heart className={`w-4 h-4 ${isFavorite ? 'fill-pink-500' : ''}`} />
                <span className="text-sm">Favorite</span>
              </button>
              <button type="button" onClick={() => setOnWishlist(!onWishlist)} className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-default ${onWishlist ? 'bg-amber-100 text-amber-800 border border-amber-300' : 'btn-light-bordered'}`}>
                <Star className={`w-4 h-4 ${onWishlist ? 'fill-amber-500 text-amber-500' : ''}`} />
                <span className="text-sm">Wishlist</span>
              </button>
              <button type="button" onClick={() => setIsRareLE(!isRareLE)} className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-default ${isRareLE ? 'bg-purple-100 text-purple-800 border border-purple-300' : 'btn-light-bordered'}`}>
                <AppIcons.RareLE className={`w-4 h-4 ${isRareLE ? 'fill-purple-500' : ''}`} />
                <span className="text-sm">Rare/LE</span>
              </button>
              <button type="button" onClick={() => setNeedsInfo(!needsInfo)} className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-default ${needsInfo ? 'bg-blue-100 text-blue-800 border border-blue-300' : 'btn-light-bordered'}`}>
                <Info className={`w-4 h-4 ${needsInfo ? 'fill-blue-500' : ''}`} />
                <span className="text-sm">Needs Info</span>
              </button>
              <button type="button" onClick={() => setIsPrivate(!isPrivate)} title="Hide from public profile, brand pages, community aggregations, and Discover" className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-default ${isPrivate ? 'bg-slate-100 text-slate-700 border border-slate-400' : 'btn-light-bordered'}`}>
                <span className="text-sm">🔒 Private</span>
              </button>
            </div>
            {isPrivate && isForDestash && (
              <div className="mt-1 text-xs text-amber-600 italic font-body">
                This item is also listed for Destash — buyers can still see it on Marketplace.
              </div>
            )}

            {onWishlist && (
              <div className="pt-3 divider-light">
                <p className="text-xs text-light-muted mb-2 font-medium">How badly do you want this?</p>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setIsoPriority('iso')} className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-default ${isoPriority === 'iso' ? PriorityColors.iso.badge : 'btn-light-bordered'}`}>{PriorityColors.iso.label}</button>
                  <button type="button" onClick={() => setIsoPriority('diso')} className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-default ${isoPriority === 'diso' ? PriorityColors.diso.badge : 'btn-light-bordered'}`}>{PriorityColors.diso.label}</button>
                  <button type="button" onClick={() => setIsoPriority('unicorn')} className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-default ${isoPriority === 'unicorn' ? PriorityColors.unicorn.badge : 'btn-light-bordered'}`}>{PriorityColors.unicorn.label}</button>
                </div>
              </div>
            )}
          </div>

          {/* Destash */}
          <div className="space-y-4">
            <h3 className="section-header-light font-body">🏪 List for Destash</h3>
            <div className={`border rounded-lg p-4 transition-default ${isForDestash ? 'border-green-300 bg-green-50' : 'border-gray-300/60'}`}>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={isForDestash} onChange={(e) => { setIsForDestash(e.target.checked); if (e.target.checked) { setDestashQuantityAvailable(quantity || 1); } else { setDestashCondition(''); setDestashPrice(''); setIsOpenToSwaps(false); setDestashNotes(''); setDestashQuantityAvailable(quantity || 1); }}} className="w-5 h-5 rounded text-green-500 focus:ring-green-500" />
                <Store className={`w-5 h-5 ${isForDestash ? 'text-green-600' : 'text-gray-500'}`} />
                <span className={`font-medium font-body ${isForDestash ? 'text-green-800' : 'text-light-secondary'}`}>List on Marketplace</span>
              </label>
              {isForDestash && (
                <div className="mt-4 space-y-4 pt-4 border-t border-green-200">
                  {quantity > 1 && (
                    <div>
                      <label className="block text-sm font-bold text-light-secondary mb-2 font-body">How many to list? <span className="text-xs text-light-muted font-normal">(You have {quantity})</span></label>
                      <div className="flex flex-wrap gap-2">
                        {Array.from({ length: quantity }, (_, i) => i + 1).map((num) => (
                          <button key={num} type="button" onClick={() => setDestashQuantityAvailable(num)} className={`w-10 h-10 rounded-lg font-bold transition-default ${destashQuantityAvailable === num ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>{num}</button>
                        ))}
                      </div>
                      {destashQuantityAvailable < quantity && <p className="mt-2 text-sm text-light-secondary">✨ Listing {destashQuantityAvailable}, keeping {quantity - destashQuantityAvailable}</p>}
                    </div>
                  )}
                  <ConditionSelector itemType="tool" value={destashCondition} onChange={setDestashCondition} required />
                  <div>
                    <label className="block text-sm font-bold text-light-secondary mb-1 font-body">Price <span className="text-red-500">*</span></label>
                    <div className="relative w-36">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-light-muted">{currency.symbol}</span>
                      <input type="number" min="0" step="0.01" value={destashPrice} onChange={(e) => setDestashPrice(e.target.value)} className={`w-full ${symbolPad} pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${!destashPrice ? 'border-amber-300' : 'input-light'}`} placeholder="0.00" />
                    </div>
                    {!destashPrice && <p className="mt-1 text-xs text-amber-600">Please enter a price</p>}
                    <p className="mt-1 text-xs text-gray-700 italic">
                      Shown to buyers in <strong>{currency.label}</strong>. Change your currency in <a href="/settings" className="underline hover:text-purple-600">Settings</a>.
                    </p>
                  </div>
                  <label className="flex items-center gap-3 cursor-pointer"><input type="checkbox" checked={isOpenToSwaps} onChange={(e) => setIsOpenToSwaps(e.target.checked)} className="w-5 h-5 rounded text-blue-500 focus:ring-blue-500" /><Repeat className="icon-sm text-blue-500" /><span className="font-medium text-light-secondary font-body">Open to Swaps</span><span className="text-xs text-light-muted">(optional)</span></label>
                  <div><label className="block text-sm font-bold text-light-secondary mb-1 font-body">Destash Notes <span className="text-xs text-light-muted font-normal">(optional)</span></label><textarea value={destashNotes} onChange={(e) => setDestashNotes(e.target.value)} rows={2} className="textarea-light focus:ring-green-500" placeholder="e.g., Half used, smells amazing..." /></div>
                  <p className="text-xs text-light-muted flex items-center gap-1"><span>⚠️</span>This item will be visible to other users on the marketplace</p>
                </div>
              )}
            </div>
          </div>

          {isSubmitting && uploadProgress > 0 && uploadProgress < 100 && (
            <div className="space-y-1"><div className="flex justify-between text-sm text-light-secondary"><span>Uploading photos...</span><span>{uploadProgress}%</span></div><div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden"><div className={`h-full ${CategoryColors.cuticle_care.bg} transition-slow rounded-full`} style={{ width: `${uploadProgress}%` }} /></div></div>
          )}

          <div className="flex gap-3 pt-4 divider-light">
            <button type="button" onClick={onClose} className="flex-1 btn-light-secondary font-body">Cancel</button>
            <button type="submit" disabled={isSubmitting || !name.trim() || !brand.trim() || (isForDestash && (!destashCondition || !destashPrice)) || (pendingPhotos.length > 0 && !photoRightsConfirmed)} className="flex-1 btn-light-primary disabled:opacity-50 disabled:cursor-not-allowed font-body">{isSubmitting ? 'Adding...' : isForDestash ? '✓ Add & List on Marketplace' : '✓ Add Cuticle Care'}</button>
          </div>
        </form>
        </>
        )}
      </div>
      {showStorageUpgradeModal && (
        <StorageUpgradeModal
          isOpen={showStorageUpgradeModal}
          onClose={() => setShowStorageUpgradeModal(false)}
        />
      )}
    </div>
  );
}
